import React, { useState, useEffect } from 'react';
import { 
  PlusCircle, 
  Edit2, 
  Trash2, 
  X, 
  Image as ImageIcon,
  Calendar,
  MapPin,
  User,
  Upload,
  Link as LinkIcon,
} from 'lucide-react';
import englishService from '../services/englishService';

// Custom Alert Component
const Alert = ({ children, variant, className = '' }) => {
  const baseStyles = 'flex w-full rounded-lg border p-4';
  const variantStyles = variant === 'destructive' 
    ? 'border-red-200 bg-red-50 text-red-600'
    : 'border-gray-200 bg-gray-50 text-gray-600';

  return (
    <div className={`${baseStyles} ${variantStyles} ${className}`}>
      {children}
    </div>
  );
};

const AlertDescription = ({ children, className = '' }) => (
  <div className={`text-sm ${className}`}>
    {children}
  </div>
);

const EventManagement = () => {
  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const eventTypes = ['conference', 'seminar', 'workshop', 'meeting', 'other'];

  const [formData, setFormData] = useState({
    title: '',
    thumbnail: '',
    content: [''],
    date: new Date().toISOString().split('T')[0],
    AnyLink: '',
    location: '',
    organizer: '',
    eventType: 'other',
    registrationRequired: false,
    publishedBy: '',
    images: []
  });

useEffect(() => {
  console.log('Component mounted, fetching events...');
  fetchEvents();
}, []);

const fetchEvents = async () => {
  try {
    setLoading(true);
    console.log('Making API call to fetch events...');
    const response = await englishService.events.getAll();
    console.log('Events response:', response);
    if (response.data) {
      setEvents(response.data);
      console.log('Events set in state:', response.data);
    }
  } catch (error) {
    console.error('Failed to fetch events:', error);
    setError('Failed to fetch events');
  } finally {
    setLoading(false);
  }
};

  const handleInputChange = (e, index) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'content') {
      const newContent = [...formData.content];
      newContent[index] = value;
      setFormData(prev => ({ ...prev, content: newContent }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'AnyLink') {
      setFormData(prev => ({ ...prev, AnyLink: value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAddParagraph = () => {
    setFormData(prev => ({
      ...prev,
      content: [...prev.content, '']
    }));
  };

  const handleRemoveParagraph = (index) => {
    setFormData(prev => ({
      ...prev,
      content: prev.content.filter((_, i) => i !== index)
    }));
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'thumbnail') {
          setFormData(prev => ({ ...prev, thumbnail: reader.result }));
        } else if (type === 'image') {
          if (formData.images.length >= 5) {
            setError('Maximum 5 images are allowed');
            return;
          }
          setFormData(prev => ({
            ...prev,
            images: [...prev.images, reader.result]
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return false;
    }
    if (!formData.thumbnail) {
      setError('Thumbnail is required');
      return false;
    }
    if (!formData.content[0]) {
      setError('Content is required');
      return false;
    }
    if (!formData.location) {
      setError('Location is required');
      return false;
    }
    if (!formData.eventType) {
      setError('Event type is required');
      return false;
    }
    if (formData.images.length > 5) {
      setError('Maximum 5 images are allowed');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
  
    try {
      setLoading(true);
      setError('');
  
      const formDataToSubmit = {
        title: formData.title.trim(),
        eventType: formData.eventType?.trim() || '',
        thumbnail: formData.thumbnail,
        content: formData.content.filter(paragraph => paragraph.trim() !== ''),
        AnyLink: formData.AnyLink || '',
        date: new Date(formData.date).toISOString(),
        location: formData.location?.trim() || '',
        organizer: formData.organizer?.trim() || '',
        images: formData.images || [],
      };
  
      let response;
      if (selectedEvent) {
        response = await englishService.events.update(selectedEvent._id, formDataToSubmit);
        setSuccess('Event updated successfully!');
      } else {
        response = await englishService.events.create(formDataToSubmit);
        console.log('Event created successfully:', response);
        setSuccess('Event created successfully!');
      }
  
      console.log('Fetching updated events list...');
      await fetchEvents(); // Fetch the updated list
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Error submitting event:', error);
      setError(error.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };
  
  const handleEdit = (event) => {
    setSelectedEvent(event);
    setFormData({
      title: event.title,
      content: event.content,
      thumbnail: event.thumbnail,
      date: new Date(event.date).toISOString().split('T')[0],
      location: event.location,
      organizer: event.organizer || '',
      eventType: event.eventType,
      registrationRequired: event.registrationRequired,
      AnyLink: event.AnyLink || '',
      publishedBy: event.publishedBy || '',
      images: event.images || []
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await englishService.events.delete(selectedEvent._id);
      setSuccess('Event deleted successfully!');
      fetchEvents();
      setShowDeleteModal(false);
      setSelectedEvent(null);
    } catch (error) {
      setError(error.message || 'Failed to delete event');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      thumbnail: '',
      content: [''],
      date: new Date().toISOString().split('T')[0],
      location: '',
      organizer: '',
      eventType: 'other',
      registrationRequired: false,
      AnyLink: '',
      publishedBy: '',
      images: []
    });
    setSelectedEvent(null);
  };

  return (
    <div className="min-h-screen bg-white p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Event Management</h1>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors"
        >
          <PlusCircle className="mr-2" size={20} />
          Add Event
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {success && (
        <Alert className="mb-4 bg-green-50 text-green-700 border-green-200">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Events List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {events.map((event) => (
          <div key={event._id} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
            <div className="h-48 overflow-hidden">
              <img
                src={event.thumbnail || '/api/placeholder/400/200'}
                alt={event.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-4">
              <div className="flex items-center mb-2">
                <span className={`px-2 py-1 text-sm rounded ${
                  event.eventType === 'conference' ? 'bg-purple-100 text-purple-600' :
                  event.eventType === 'seminar' ? 'bg-blue-100 text-blue-600' :
                  event.eventType === 'workshop' ? 'bg-green-100 text-green-600' :
                  event.eventType === 'meeting' ? 'bg-yellow-100 text-yellow-600' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {event.eventType.charAt(0).toUpperCase() + event.eventType.slice(1)}
                </span>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">{event.title}</h3>
              <div className="space-y-2 text-gray-600 text-sm">
                <div className="flex items-center">
                  <Calendar size={16} className="mr-2" />
                  {new Date(event.date).toLocaleDateString()}
                </div>
                <div className="flex items-center">
                  <MapPin size={16} className="mr-2" />
                  {event.location}
                </div>
                {event.organizer && (
                  <div className="flex items-center">
                    <User size={16} className="mr-2" />
                    {event.organizer}
                  </div>
                )}
              </div>
              {event.registrationRequired && event.AnyLink && (
                <a
                  href={event.AnyLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center mt-4 text-sky-500 hover:text-sky-600"
                >
                  <LinkIcon size={16} className="mr-1" />
                  Register Now
                </a>
              )}
              <div className="flex justify-end space-x-2 mt-4">
                <button
                  onClick={() => handleEdit(event)}
                  className="p-2 text-sky-500 hover:bg-sky-50 rounded-full transition-colors"
                >
                  <Edit2 size={20} />
                </button>
                <button
                  onClick={() => {
                    setSelectedEvent(event);
                    setShowDeleteModal(true);
                  }}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  {selectedEvent ? 'Edit Event' : 'Add Event'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    required
                  />
                </div>

                {/* Event Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Type *
                  </label>
                  <select
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    required
                  >
                    {eventTypes.map(type => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Thumbnail */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thumbnail *
                  </label>
                  <div className="flex items-center space-x-4">
                    {formData.thumbnail && (
                      <img
                        src={formData.thumbnail}
                        alt="Thumbnail preview"
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <label className="flex flex-col items-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-sky-500">
                        <ImageIcon className="text-gray-400 mb-2" size={24} />
                        <span className="text-sm text-gray-600">Upload thumbnail</span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, 'thumbnail')}
                          required={!formData.thumbnail}
                        />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Content Paragraphs */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <label className="block text-sm font-medium text-gray-700">Content *
                  </label>
                    <button
                      type="button"
                      onClick={handleAddParagraph}
                      className="text-sky-500 hover:text-sky-600"
                    >
                      + Add Paragraph
                    </button>
                  </div>
                  {formData.content.map((paragraph, index) => (
                    <div key={index} className="relative">
                      <textarea
                        name="content"
                        value={paragraph}
                        onChange={(e) => handleInputChange(e, index)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 min-h-[100px]"
                        required
                      />
                      {formData.content.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveParagraph(index)}
                          className="absolute top-2 right-2 text-red-500 hover:text-red-600"
                        >
                          <X size={20} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Any Link */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Any Link
                  </label>
                  <input
                    type="text"
                    name="AnyLink"
                    value={formData.AnyLink}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  />
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date *
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    required
                  />
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location *
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    required
                  />
                </div>

                {/* Organizer */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organizer
                  </label>
                  <input
                    type="text"
                    name="organizer"
                    value={formData.organizer}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  />
                </div>

                {/* Registration */}
                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="registrationRequired"
                      checked={formData.registrationRequired}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-sky-500 border-gray-300 rounded focus:ring-sky-500"
                    />
                    <label className="ml-2 text-sm font-medium text-gray-700">
                      Registration Required
                    </label>
                  </div>
                  {formData.registrationRequired && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Any Link *
                      </label>
                      <input
                        type="url"
                        name="AnyLink"
                        value={formData.AnyLink}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                        required
                      />
                    </div>
                  )}
                </div>

                {/* Additional Images */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Images (Max 5)
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.images.map((image, index) => (
                      <div key={index} className="relative">
                        <img
                          src={image}
                          alt={`Additional ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              images: prev.images.filter((_, i) => i !== index)
                            }));
                          }}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                    {formData.images.length < 5 && (
                      <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-sky-500">
                        <Upload className="text-gray-400 mb-2" size={24} />
                        <span className="text-sm text-gray-600">Add image</span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, 'image')}
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : (selectedEvent ? 'Update Event' : 'Create Event')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Delete Event</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{selectedEvent.title}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && !showModal && !showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventManagement;